import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { downloadFile } from '../../../../../../services/axios/utility/download-file';
import { Button } from '../../../../button/button';
import { Icon } from '../../../../icon/icon';
import { InfoBox } from '../../../../info-box/info-box';
import { ProgressBar } from '../../../../progress-bar/progress-bar';
import { Stack } from '../../../../stack/stack';
import { CadDrawingReportOmitted } from './cad-drawing-report-omitted';
import { ReportExportError, ReportExportState } from './report-export';

type Props = {
  state: ReportExportState;
};

/**
 * The progress of the CAD drawing report's export, and its result: page 1 of the PDF and a
 * download button.
 */
export const CadDrawingReportExportStatus = ({ state }: Props) => {
  const { t } = useTranslation();

  switch (state.status) {
    case 'idle':
      return null;

    case 'preparing':
    case 'collecting':
      return (
        <ProgressBar
          max={1}
          min={0}
          text={
            state.status === 'preparing'
              ? t('cadDrawingReport.export.preparing', { ns: 'skyviewReport' })
              : t('cadDrawingReport.export.collecting', { ns: 'skyviewReport' })
          }
          value={0}
        />
      );

    case 'uploading':
    case 'rendering':
      return (
        <ProgressBar
          max={1}
          min={0}
          text={
            state.status === 'uploading'
              ? t('cadDrawingReport.export.uploading', { ns: 'skyviewReport' })
              : t('cadDrawingReport.export.rendering', { ns: 'skyviewReport' })
          }
          value={state.progress}
        />
      );

    case 'failed':
      return <InfoBox color="red">{getErrorText(t, state.error)}</InfoBox>;

    case 'completed':
      return (
        <CompletedLayout $fitToPreview={!!state.thumbnailUrl} spacing={0.5}>
          {state.thumbnailUrl ? (
            <Thumbnail
              alt={t('cadDrawingReport.export.thumbnail', { ns: 'skyviewReport' })}
              src={state.thumbnailUrl}
            />
          ) : (
            <Stack alignItems="center" direction="row" spacing={0.5}>
              <Icon icon={['fal', 'file-pdf']} size="3x" />
              <InfoBox color="yellow">
                {state.thumbnailSkipped === 'too-large'
                  ? t('cadDrawingReport.export.thumbnailTooLarge', { ns: 'skyviewReport' })
                  : t('cadDrawingReport.export.thumbnailFailed', { ns: 'skyviewReport' })}
              </InfoBox>
            </Stack>
          )}
          <CadDrawingReportOmitted exported={true} omitted={state.omitted} />
          {state.resultSaved && (
            <InfoBox color="green">
              {t('cadDrawingReport.export.saved', { ns: 'skyviewReport' })}
            </InfoBox>
          )}
          <Button
            color="secondary"
            leftIcon={{ icon: 'download' }}
            variant="contained"
            width="100%"
            onClick={() => void downloadFile(state.downloadUrl, state.fileName)}
          >
            {t('cadDrawingReport.export.download', { ns: 'skyviewReport' })}
          </Button>
        </CompletedLayout>
      );
  }
};

function getErrorText(t: TFunction, error: ReportExportError) {
  switch (error) {
    case 'payload':
      return t('cadDrawingReport.export.errors.payload', { ns: 'skyviewReport' });
    case 'access':
      return t('cadDrawingReport.export.errors.access', { ns: 'skyviewReport' });
    case 'too-large':
      return t('cadDrawingReport.export.errors.tooLarge', { ns: 'skyviewReport' });
    case 'upload':
      return t('cadDrawingReport.export.errors.upload', { ns: 'skyviewReport' });
    case 'timeout':
      return t('cadDrawingReport.export.errors.timeout', { ns: 'skyviewReport' });
    case 'internal':
      return t('cadDrawingReport.export.errors.internal', { ns: 'skyviewReport' });
  }
}

/** With a preview, as wide as it, so the texts below it wrap instead of widening the dialog. */
const CompletedLayout = styled(Stack)<{ $fitToPreview: boolean }>`
  ${({ $fitToPreview }) => $fitToPreview && 'width: min-content;'}
`;

/**
 * The page at most of the screen's height, whichever way it is turned, and as wide as that makes
 * it, up to the screen's width.
 */
const Thumbnail = styled.img`
  display: block;
  height: 65vh;
  width: auto;
  max-width: calc(90vw - 3em);
  object-fit: contain;
  filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.35));
`;
